import type { AdminRouterContext } from '@/lib/router-context'
import type { Location } from '@/lib/services/locations/locations.types'
import { RecordHistoryPage } from '@/components/audit-log/RecordHistoryPage'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { auditLogQueries } from '@/lib/services/audit-log/audit-log.query'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import { createFileRoute, notFound } from '@tanstack/react-router'

const filters = locationFilters().get()

export const Route = createFileRoute(
  '/_authenticated/$orgId/settings/locations/$locationId/history'
)({
  component: LocationHistoryPage,
  head: ({ loaderData, params }) =>
    adminTitleHead(
      'History',
      loaderData ? getLocationName(loaderData.location) : `Location #${params?.locationId}`,
      'Locations',
      'Settings'
    ),
  loader: loadLocationHistory,
})

export async function loadLocationHistory({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number | string; locationId: number | string }
}): Promise<{ location: Location }> {
  if (!context.ability.can('view', 'audit-logs')) throw notFound()

  const [location] = await Promise.all([
    context.queryClient.ensureQueryData(
      locationQueries.show({
        organizationId: params.orgId,
        locationId: params.locationId,
        filters,
      })
    ),
    context.queryClient.ensureInfiniteQueryData(
      auditLogQueries.record({
        organizationId: params.orgId,
        subject: 'locations',
        recordId: params.locationId,
      })
    ),
  ])

  return { location }
}

function LocationHistoryPage() {
  const { locationId } = Route.useParams()
  const { location } = Route.useLoaderData()
  const path = usePathHelper()

  return (
    <RecordHistoryPage
      backLabel="location"
      backLink={path.locations.show(locationId).link}
      description="Review important changes to this location and its delivery pricing."
      organizationId={location.organization_id}
      recordId={locationId}
      recordLabel={getLocationName(location)}
      subject="locations"
    />
  )
}
